import {
  Box,
  FormControl,
  FormLabel,
  HStack,
  Input,
  FormErrorMessage,
  Heading,
} from "@chakra-ui/react";
import { cloneDeep } from "lodash";
import { ChangeEvent, useEffect, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { useSelector } from "react-redux";
import {
  CAMPAIGN_QUALIFICATION_RULES,
  QUALIFICATION_RULES_OPTIONS,
} from "../../../common/constants/campaign";
import { CampaignRuleResp } from "../../../common/types/campaign";
import DropdownWithSearch from "../../../components/DropdownWithSearch";
import IconWithTooltip from "../../../components/IconWithTooltip";
import { selectCampaign } from "./campaignSlice";

export default function CampaignQualification({
  onChange,
  data,
  setRuleValid,
}: {
  onChange: (formValue: CampaignRuleResp) => void;
  data: CampaignRuleResp;
  setRuleValid: (valid: boolean) => void;
}) {
  const { activeErrorCheck } = useSelector(selectCampaign);

  const [errors, setErrors] = useState({
    rule: "",
    recurring_period: "",
  });

  const frequencies = {
    days: "Days",
    weeks: "Weeks",
    months: "Months",
  };

  useEffect(() => {
    const rule = data.rule;
    if (rule !== CAMPAIGN_QUALIFICATION_RULES.ONCE_EVERY) {
      if (!Object.values(CAMPAIGN_QUALIFICATION_RULES).includes(rule)) {
        setErrors({ rule: "Select an option", recurring_period: "" });
        setRuleValid(false);
      } else {
        setErrors({ rule: "", recurring_period: "" });
        setRuleValid(true);
      }
    } else {
      setErrors((prevState) => ({
        ...prevState,
        recurring_period:
          data.recurring_period && data.recurring_period_unit
            ? ""
            : "Invalid period",
      }));
      setRuleValid(!!(data.recurring_period && data.recurring_period_unit));
    }
  }, [data, setRuleValid]);

  function handleRuleChange(rule: CAMPAIGN_QUALIFICATION_RULES) {
    const newValue = cloneDeep(data);
    if (rule !== CAMPAIGN_QUALIFICATION_RULES.ONCE_EVERY) {
      newValue.recurring_period = null;
      newValue.recurring_period_unit = null;
    }
    newValue.rule = rule;
    onChange(newValue);
  }

  function handlePeriodChange(event: ChangeEvent<HTMLInputElement>) {
    const newValue = cloneDeep(data);
    const period = +event.target.value;
    newValue.recurring_period = period;
    onChange(newValue);
  }

  function handlePeriodUnitChange(periodUnit: string) {
    const newValue = cloneDeep(data);
    newValue.recurring_period_unit = periodUnit;
    onChange(newValue);
  }

  return (
    <Box w="xl">
      <HStack mb="2">
        <Heading as="h3" color="gray.600" fontSize="sm">
          Journey Qualification
        </Heading>

        <IconWithTooltip
          label="Qualification rules determine how many times each person can run
          through the journey flows"
          icon={FaInfoCircle}
          fontSize="xs"
        />
      </HStack>

      <form id="qualification-form">
        <FormControl isInvalid={activeErrorCheck && !!errors.rule}>
          <FormLabel color="gray.500" fontSize="sm">
            Run each person through
          </FormLabel>
          <DropdownWithSearch
            options={Object.entries(QUALIFICATION_RULES_OPTIONS).map(
              ([rule, label]) => {
                return { label: label, value: rule };
              }
            )}
            value={
              data.rule
                ? Object.entries(QUALIFICATION_RULES_OPTIONS)
                    .filter(([rule, _]) => rule === data.rule)
                    .map(([rule, label]) => {
                      return { label: label, value: rule };
                    })
                : undefined
            }
            onChange={(option) =>
              handleRuleChange(option?.value as CAMPAIGN_QUALIFICATION_RULES)
            }
            isInvalid={activeErrorCheck && !!errors.rule}
          />
          <FormErrorMessage fontSize="12px">{errors.rule}</FormErrorMessage>
        </FormControl>

        {data.rule === CAMPAIGN_QUALIFICATION_RULES.ONCE_EVERY && (
          <HStack spacing="3">
            <FormControl
              isInvalid={activeErrorCheck && !!errors.recurring_period}
            >
              <FormLabel color="gray.500" fontSize="xs">
                Run every
              </FormLabel>
              <HStack>
                <Input
                  name="recurring_period"
                  onChange={handlePeriodChange}
                  value={data.recurring_period ?? ""}
                  type="number"
                />
                <DropdownWithSearch
                  options={Object.entries(frequencies).map(
                    ([frequency, label]) => {
                      return { label: label, value: frequency };
                    }
                  )}
                  value={
                    data.recurring_period_unit
                      ? Object.entries(frequencies)
                          .filter(
                            ([frequency, _]) =>
                              frequency === data.recurring_period_unit
                          )
                          .map(([frequency, label]) => {
                            return { label: label, value: frequency };
                          })
                      : undefined
                  }
                  onChange={(option) =>
                    handlePeriodUnitChange(option?.value ?? "")
                  }
                  isSearchable={false}
                  isInvalid={activeErrorCheck && !!errors.recurring_period}
                  controlStyle={{ width: "252px" }}
                />
              </HStack>
              <FormErrorMessage fontSize="12px">
                {errors.recurring_period}
              </FormErrorMessage>
            </FormControl>
          </HStack>
        )}
      </form>
    </Box>
  );
}
